import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { queryKeys } from '@/api/queryKeys';
import type { Label, LabelColor, LabelInput, LabelPatch } from '@/api/types';
import {
  labelMigration,
  labelMigrationsVersion,
  startLabelMigration,
  subscribeLabelMigrations,
  type LabelMigration,
} from './migrations';

/**
 * The label list of ONE mailbox (plan/17 §5), and its verbs.
 *
 * Every reader of it — the sidebar section, the chips, the picker, the
 * filters — goes through `useLabels`, one query per account, so a
 * `labels_changed` frame (which invalidates exactly that key) repaints all of
 * them at once.
 *
 * It is also where an unfinished rename or delete is noticed: a list that
 * comes back with a label not `active` starts that label's migrate loop
 * (`migrations.ts`), once, whichever component loaded it.
 */
export function useLabels(accountId: string | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.account(accountId ?? '').labels(),
    queryFn: ({ signal }) => api.labels.list(accountId ?? '', signal),
    enabled: accountId !== undefined && accountId !== '',
    staleTime: STALE_TIMES.metadata,
  });
  const data = query.data;
  useEffect(() => {
    if (!data || accountId === undefined) return;
    for (const label of data) {
      if (label.state !== 'active') void startLabelMigration(queryClient, api, accountId, label);
    }
  }, [accountId, api, data, queryClient]);
  return query;
}

/** `useQueries`' combine: only the data, and a stable reference while it is unchanged. */
function listsOf(results: readonly { data: Label[] | undefined }[]): (Label[] | undefined)[] {
  return results.map((result) => result.data);
}

/** Several mailboxes' lists at once — the unified list's chips, per row's account. */
export function useLabelsByAccount(accountIds: readonly string[]): ReadonlyMap<string, Label[]> {
  const api = useApi();
  const queryClient = useQueryClient();
  const lists = useQueries({
    queries: accountIds.map((accountId) => ({
      queryKey: queryKeys.account(accountId).labels(),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.labels.list(accountId, signal),
      staleTime: STALE_TIMES.metadata,
    })),
    combine: listsOf,
  });
  // A rename or delete left unfinished (another tab closed mid-way) resumes
  // wherever a list is loaded, as `useLabels` does; one loop per label.
  useEffect(() => {
    accountIds.forEach((accountId, index) => {
      for (const label of lists[index] ?? []) {
        if (label.state !== 'active') void startLabelMigration(queryClient, api, accountId, label);
      }
    });
  }, [accountIds, api, lists, queryClient]);
  return useMemo(() => {
    const map = new Map<string, Label[]>();
    accountIds.forEach((accountId, index) => {
      const labels = lists[index];
      if (labels) map.set(accountId, labels);
    });
    return map;
  }, [accountIds, lists]);
}

/** The live progress of one label's rename/delete loop, if one is running. */
export function useLabelMigration(labelId: string): LabelMigration | undefined {
  useSyncExternalStore(subscribeLabelMigrations, labelMigrationsVersion, labelMigrationsVersion);
  return labelMigration(labelId);
}

/** Replaces one row of the cached list with the server's answer. */
function writeRow(list: Label[] | undefined, row: Label): Label[] | undefined {
  if (!list) return list;
  return list.some((label) => label.id === row.id)
    ? list.map((label) => (label.id === row.id ? row : label))
    : [...list, row];
}

/**
 * Create, rename, recolour, show/hide, reorder, delete. Each is ONE request;
 * the cached list takes the server's answer, and a rename or delete then
 * starts the migrate loop that rewrites the messages.
 */
export function useLabelMutations(accountId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const key = queryKeys.account(accountId).labels();
  const settle = () => queryClient.invalidateQueries({ queryKey: key });

  const create = useMutation({
    mutationFn: (input: LabelInput) => api.labels.create(accountId, input),
    onSuccess: (label) => queryClient.setQueryData<Label[]>(key, (list) => writeRow(list, label)),
    onSettled: settle,
  });

  const update = useMutation({
    mutationFn: ({ label, patch }: { label: Label; patch: Omit<LabelPatch, 'version'> }) =>
      api.labels.update(accountId, label.id, { version: label.version, ...patch }),
    onSuccess: (label) => {
      queryClient.setQueryData<Label[]>(key, (list) => writeRow(list, label));
      if (label.state !== 'active') void startLabelMigration(queryClient, api, accountId, label);
    },
    onSettled: settle,
  });

  const reorder = useMutation({
    mutationFn: (labelIds: string[]) => api.labels.reorder(accountId, labelIds),
    // The new order shows at once: a Move up that waits for the round trip
    // reads as a button that did nothing.
    onMutate: async (labelIds) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Label[]>(key);
      if (previous) {
        const byId = new Map(previous.map((label) => [label.id, label]));
        const ordered = labelIds.flatMap((id) => {
          const label = byId.get(id);
          return label ? [label] : [];
        });
        const rest = previous.filter((label) => !labelIds.includes(label.id));
        queryClient.setQueryData<Label[]>(key, [...ordered, ...rest]);
      }
      return { previous };
    },
    onError: (_error, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (labels) => queryClient.setQueryData<Label[]>(key, labels),
    onSettled: settle,
  });

  const remove = useMutation({
    mutationFn: (label: Label) => api.labels.remove(accountId, label.id),
    onSuccess: (label) => {
      queryClient.setQueryData<Label[]>(key, (list) => writeRow(list, label));
      void startLabelMigration(queryClient, api, accountId, label);
    },
    onSettled: settle,
  });

  return { create, update, reorder, remove };
}

/** A label's CSS colour tokens, for a chip or a dot. */
export function labelColorVars(color: LabelColor): Record<string, string> {
  return {
    '--label-color': `var(--color-label-${color})`,
    '--label-soft': `var(--color-label-${color}-soft)`,
    '--label-ink': `var(--color-label-${color}-ink)`,
  };
}

export const LABEL_COLOR_NAMES: Record<LabelColor, string> = {
  gray: 'Grey',
  blue: 'Blue',
  teal: 'Teal',
  green: 'Green',
  amber: 'Amber',
  orange: 'Orange',
  red: 'Red',
  violet: 'Violet',
  pink: 'Pink',
};
